import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import 'reset-css';
import 'swiper/css';
import './scss/index.scss';
import { persistor, store } from './store/index';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Web3AuthProvider, useWeb3Auth } from './auth/Web3AuthContext';
import { StackNavigator } from './navigation/StackNavigator';
import { RootState, useAppDispatch } from './store';
import { cleanupBalanceSubscription, subscribeToBalance } from './store/slices/balanceSlice';
import { PWAInstallProvider } from './contexts/PWAInstallContext';
import { ErrorBoundary, PWAInstallBanner } from './components';
import { useAutomatedDrawEntry } from './hooks';
import { subscribeToEnterDrawStatus } from './firebase';
import StatusModal from './components/StatusModal';

const AppContent = () => {
  const dispatch = useAppDispatch();
  const userEmail = useSelector((state: RootState) => state.userSlice.user?.email);
  const isAuthenticated = useSelector((state: RootState) => state.authSlice.isAuthenticated);
  const { isFirebaseReady } = useWeb3Auth();
  const [canEnterDraw, setCanEnterDraw] = useState<boolean>(false);
  const [sessionStatusModal, setSessionStatusModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
  });

  // Subscribe to enter_draw status — must wait for Firebase Auth session
  useEffect(() => {
    if (!userEmail || !isFirebaseReady) {
      setCanEnterDraw(false);
      return;
    }

    const unsubscribe = subscribeToEnterDrawStatus(
      userEmail,
      (status) => {
        setCanEnterDraw(status);
      },
      (error) => {
        console.error("Error getting draw status:", error);
        setCanEnterDraw(false);
      }
    );

    return () => unsubscribe();
  }, [userEmail, isFirebaseReady]);

  // Automated draw entry is always enabled.
  const automatedDrawEntry = useAutomatedDrawEntry(
    userEmail,
    isAuthenticated && isFirebaseReady,
    canEnterDraw
  );

  useEffect(() => {
    if (userEmail && isAuthenticated && isFirebaseReady) {
      dispatch(subscribeToBalance(userEmail));
    }

    return () => {
      cleanupBalanceSubscription();
    };
  }, [userEmail, isAuthenticated, isFirebaseReady, dispatch]);

  useEffect(() => {
    const result = automatedDrawEntry.lastResult;
    if (!result || result.success) {
      return;
    }

    const errorMessage = String(result.error || '');
    const isSessionFailure = /session|sign in again|provider|disconnected|login/i.test(errorMessage);
    if (!isSessionFailure) {
      return;
    }

    setSessionStatusModal({
      isOpen: true,
      title: 'Session Expired',
      message: 'Your wallet session expired while preparing the automatic draw transfer. Please sign in again and retry.',
    });
  }, [automatedDrawEntry.lastResult]);

  return (
    <>
      <StackNavigator />
      {isAuthenticated && <PWAInstallBanner />}
      <StatusModal
        isOpen={sessionStatusModal.isOpen}
        onClose={() => setSessionStatusModal((prev) => ({ ...prev, isOpen: false }))}
        title={sessionStatusModal.title}
        message={sessionStatusModal.message}
      />
    </>
  );
};

function App() {
  return (
    <div id='app'>
      <ErrorBoundary>
        <Web3AuthProvider>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <PWAInstallProvider>
                <AppContent />
              </PWAInstallProvider>
            </PersistGate>
          </Provider>
        </Web3AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;